import React, { useState } from 'react';
import { Lightbulb, Power, Plus, X, Clock, Edit2, Trash2, Save } from 'lucide-react';

interface SmartDevice {
  id: string;
  name: string;
  type: 'LIGHT' | 'PLUG' | 'SENSOR' | 'MOTION';
  brand: string;
  location: string;
  status: 'connected' | 'disconnected' | 'pairing';
  lastSeen?: Date;
}

interface Automation {
  id: string;
  name: string;
  condition: {
    type: 'TIME' | 'MOTION' | 'PRESSURE' | 'LIGHT';
    sensor?: string;
    timeRange?: {
      start: string;
      end: string;
    };
    location?: string;
  };
  action: {
    deviceId: string;
    command: 'ON' | 'OFF' | 'DIM' | 'FLASH';
    value?: number;
  };
  status: 'active' | 'inactive';
}

const SmartHomeIntegration: React.FC = () => {
  const [devices, setDevices] = useState<SmartDevice[]>([
    {
      id: 'light-1',
      name: 'Hallway Light',
      type: 'LIGHT',
      brand: 'Philips Hue',
      location: 'Hallway',
      status: 'connected',
      lastSeen: new Date()
    },
    {
      id: 'sensor-1',
      name: 'Bed Pressure Sensor',
      type: 'SENSOR',
      brand: 'Scanalytics',
      location: 'Bedroom',
      status: 'connected',
      lastSeen: new Date()
    }
  ]);

  const [automations, setAutomations] = useState<Automation[]>([
    {
      id: 'auto-1',
      name: 'Night Movement Lights',
      condition: {
        type: 'PRESSURE',
        sensor: 'sensor-1',
        timeRange: {
          start: '20:30',
          end: '05:00'
        },
        location: 'Bedroom'
      },
      action: {
        deviceId: 'light-1',
        command: 'ON',
        value: 50 // 50% brightness
      },
      status: 'active'
    }
  ]);

  const [showAddDevice, setShowAddDevice] = useState(false);
  const [showAddAutomation, setShowAddAutomation] = useState(false);
  const [newDevice, setNewDevice] = useState<Partial<SmartDevice>>({
    type: 'LIGHT',
    status: 'disconnected'
  });
  const [newAutomation, setNewAutomation] = useState<Partial<Automation>>({
    name: '',
    condition: { 
      type: 'TIME' as const,
      timeRange: { start: '', end: '' }
    },
    action: { 
      deviceId: '',
      command: 'ON' as const
    },
    status: 'active' as const
  });

  const handleAddDevice = () => {
    if (newDevice.name && newDevice.type && newDevice.brand && newDevice.location) {
      setDevices([
        ...devices,
        {
          ...newDevice as SmartDevice,
          id: `device-${Date.now()}`,
          status: 'pairing',
          lastSeen: new Date()
        }
      ]);
      setShowAddDevice(false);
      setNewDevice({ type: 'LIGHT', status: 'disconnected' });

      // Simulate device connecting
      setTimeout(() => {
        setDevices(devices => 
          devices.map(d => 
            d.id === `device-${Date.now()}` ? { ...d, status: 'connected' } : d
          )
        );
      }, 2000);
    }
  };

  const handleAddAutomation = () => {
    if (newAutomation.name && 
        newAutomation.condition?.type && 
        newAutomation.action?.deviceId && 
        newAutomation.action?.command &&
        (!newAutomation.condition.timeRange || 
         (newAutomation.condition.timeRange.start && newAutomation.condition.timeRange.end))) {
      setAutomations([
        ...automations,
        {
          ...newAutomation as Automation,
          id: `auto-${Date.now()}`,
          status: 'active'
        }
      ]);
      setShowAddAutomation(false);
      setNewAutomation({
        name: '',
        condition: { 
          type: 'TIME' as const,
          timeRange: { start: '', end: '' }
        },
        action: { 
          deviceId: '',
          command: 'ON' as const
        },
        status: 'active' as const
      });
    }
  };

  const handleDeleteAutomation = (id: string) => {
    setAutomations(automations.filter(a => a.id !== id));
  };

  const handleToggleAutomation = (id: string) => {
    setAutomations(automations.map(a => 
      a.id === id ? { ...a, status: a.status === 'active' ? 'inactive' : 'active' } : a
    ));
  };

  const getDeviceIcon = (type: string) => {
    switch (type) {
      case 'LIGHT':
        return <Lightbulb className="text-yellow-500" />;
      case 'PLUG':
        return <Power className="text-green-500" />;
      default:
        return <Power className="text-blue-500" />;
    }
  };

  const handleTimeRangeChange = (field: 'start' | 'end', value: string) => {
    if (newAutomation.condition?.type === 'TIME') {
      setNewAutomation({
        ...newAutomation,
        condition: {
          ...newAutomation.condition,
          timeRange: {
            start: field === 'start' ? value : newAutomation.condition.timeRange?.start || '',
            end: field === 'end' ? value : newAutomation.condition.timeRange?.end || ''
          }
        }
      });
    }
  };

  const handleActionChange = (field: 'deviceId' | 'command' | 'value', value: string | number) => {
    setNewAutomation({
      ...newAutomation,
      action: {
        deviceId: field === 'deviceId' ? value as string : (newAutomation.action?.deviceId || ''),
        command: field === 'command' ? value as Automation['action']['command'] : (newAutomation.action?.command || 'ON'),
        ...(field === 'value' ? { value: value as number } : {})
      }
    });
  };

  const handleConditionTypeChange = (value: Automation['condition']['type']) => {
    setNewAutomation({
      ...newAutomation,
      condition: {
        type: value,
        ...(value === 'TIME' ? { timeRange: { start: '', end: '' } } : {}),
        ...(newAutomation.condition?.location ? { location: newAutomation.condition.location } : {})
      } as Automation['condition']
    });
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Smart Home Integration</h2>

      {/* Devices Section */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Connected Devices</h3>
          <button
            onClick={() => setShowAddDevice(true)}
            className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <Plus size={20} className="mr-2" />
            Add Device
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {devices.map(device => (
            <div key={device.id} className="border rounded-lg p-4 relative">
              <div className="flex items-start justify-between">
                <div className="flex items-center">
                  {getDeviceIcon(device.type)}
                  <div className="ml-3">
                    <h4 className="font-medium">{device.name}</h4>
                    <p className="text-sm text-gray-500">{device.brand}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  device.status === 'connected' ? 'bg-green-100 text-green-800' :
                  device.status === 'pairing' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {device.status}
                </span>
              </div>
              <div className="mt-2 text-sm text-gray-600">
                <p>Location: {device.location}</p>
                {device.lastSeen && (
                  <p>Last seen: {device.lastSeen.toLocaleTimeString()}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Add Device Modal */}
        {showAddDevice && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold">Add New Device</h3>
                <button onClick={() => setShowAddDevice(false)}>
                  <X size={24} />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Device Type
                  </label>
                  <select
                    className="w-full border rounded-lg p-2"
                    value={newDevice.type}
                    onChange={e => setNewDevice({ ...newDevice, type: e.target.value as SmartDevice['type'] })}
                  >
                    <option value="LIGHT">Smart Light</option>
                    <option value="PLUG">Smart Plug</option>
                    <option value="SENSOR">Sensor</option>
                    <option value="MOTION">Motion Detector</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Device Name
                  </label>
                  <input
                    type="text"
                    className="w-full border rounded-lg p-2"
                    value={newDevice.name || ''}
                    onChange={e => setNewDevice({ ...newDevice, name: e.target.value })}
                    placeholder="e.g., Hallway Light"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Brand
                  </label>
                  <input
                    type="text"
                    className="w-full border rounded-lg p-2"
                    value={newDevice.brand || ''}
                    onChange={e => setNewDevice({ ...newDevice, brand: e.target.value })}
                    placeholder="e.g., Philips Hue"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    className="w-full border rounded-lg p-2"
                    value={newDevice.location || ''}
                    onChange={e => setNewDevice({ ...newDevice, location: e.target.value })}
                    placeholder="e.g., Living Room"
                  />
                </div>
                <button
                  onClick={handleAddDevice}
                  className="w-full bg-blue-500 text-white rounded-lg py-2 hover:bg-blue-600 transition-colors"
                >
                  Add Device
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Automations Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Automations</h3>
          <button
            onClick={() => setShowAddAutomation(true)}
            className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <Plus size={20} className="mr-2" />
            Add Automation
          </button>
        </div>

        <div className="space-y-4">
          {automations.map(automation => (
            <div key={automation.id} className="border rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-medium">{automation.name}</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    When: {automation.condition.type === 'TIME' ? 
                      `Between ${automation.condition.timeRange?.start} and ${automation.condition.timeRange?.end}` :
                      `${automation.condition.type} detected in ${automation.condition.location}`
                    }
                  </p>
                  <p className="text-sm text-gray-600">
                    Then: {automation.action.command} {automation.action.deviceId === 'light-1' ? 'Hallway Light' : automation.action.deviceId}
                    {automation.action.value ? ` (${automation.action.value}%)` : ''}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleToggleAutomation(automation.id)}
                    className={`px-3 py-1 rounded-full text-sm ${
                      automation.status === 'active' ? 
                      'bg-green-100 text-green-800' : 
                      'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {automation.status === 'active' ? 'Active' : 'Inactive'}
                  </button>
                  <button
                    onClick={() => handleDeleteAutomation(automation.id)}
                    className="text-red-500 hover:text-red-600"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add Automation Modal */}
        {showAddAutomation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold">Add New Automation</h3>
                <button onClick={() => setShowAddAutomation(false)}>
                  <X size={24} />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Automation Name
                  </label>
                  <input
                    type="text"
                    className="w-full border rounded-lg p-2"
                    value={newAutomation.name || ''}
                    onChange={e => setNewAutomation({ ...newAutomation, name: e.target.value })}
                    placeholder="e.g., Night Movement Lights"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trigger Type
                  </label>
                  <select
                    className="w-full border rounded-lg p-2"
                    value={newAutomation.condition?.type || 'TIME'}
                    onChange={e => handleConditionTypeChange(e.target.value as Automation['condition']['type'])}
                  >
                    <option value="TIME">Time Range</option>
                    <option value="MOTION">Motion Detected</option>
                    <option value="PRESSURE">Pressure Change</option>
                    <option value="LIGHT">Light Level</option>
                  </select>
                </div>
                {newAutomation.condition?.type === 'TIME' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Time
                      </label>
                      <input
                        type="time"
                        className="w-full border rounded-lg p-2"
                        value={newAutomation.condition?.timeRange?.start || ''}
                        onChange={e => handleTimeRangeChange('start', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Time
                      </label>
                      <input
                        type="time"
                        className="w-full border rounded-lg p-2"
                        value={newAutomation.condition?.timeRange?.end || ''}
                        onChange={e => handleTimeRangeChange('end', e.target.value)}
                      />
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    className="w-full border rounded-lg p-2"
                    value={newAutomation.condition?.location || ''}
                    onChange={e => setNewAutomation({
                      ...newAutomation,
                      condition: {
                        type: newAutomation.condition?.type || 'TIME',
                        timeRange: newAutomation.condition?.timeRange,
                        location: e.target.value,
                        ...(newAutomation.condition?.sensor ? { sensor: newAutomation.condition.sensor } : {})
                      } as Automation['condition']
                    })}
                    placeholder="e.g., Bedroom"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Action
                  </label>
                  <select
                    className="w-full border rounded-lg p-2 mb-2"
                    value={newAutomation.action?.deviceId || ''}
                    onChange={e => handleActionChange('deviceId', e.target.value)}
                  >
                    <option value="">Select Device</option>
                    {devices.map(device => (
                      <option key={device.id} value={device.id}>
                        {device.name}
                      </option>
                    ))}
                  </select>
                  <select
                    className="w-full border rounded-lg p-2"
                    value={newAutomation.action?.command || 'ON'}
                    onChange={e => handleActionChange('command', e.target.value)}
                  >
                    <option value="ON">Turn On</option>
                    <option value="OFF">Turn Off</option>
                    <option value="DIM">Dim</option>
                    <option value="FLASH">Flash</option>
                  </select>
                </div>
                {newAutomation.action?.command === 'DIM' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Brightness (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      className="w-full border rounded-lg p-2"
                      value={newAutomation.action?.value || ''}
                      onChange={e => handleActionChange('value', parseInt(e.target.value))}
                    />
                  </div>
                )}
                <button
                  onClick={handleAddAutomation}
                  className="w-full bg-blue-500 text-white rounded-lg py-2 hover:bg-blue-600 transition-colors"
                >
                  Add Automation
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SmartHomeIntegration; 